import React, { useState, useEffect, useRef } from 'react';
import { 
  Atom, 
  ChevronRight,
  ChevronLeft,
  Play,
  Pause,
  RotateCcw
} from 'lucide-react';

const MultiIonAnimationSection = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [animationKey, setAnimationKey] = useState(0);

  const phases = [
    {
      id: 0,
      title: 'Traditional Battery Problem',
      subtitle: 'Why fast charging is dangerous',
      description: 'In traditional batteries, the anode layers are like pages of a book. When ions try to enter quickly during fast charging, they accumulate on the surface forming dangerous spikes called dendrites that can cause short circuits.',
    },
    {
      id: 1,
      title: 'Multi-Ion Solution: Step 1',
      subtitle: 'Carbon Anode Structure',
      description: 'Our Carbon Anode is processed to create multiple entry pathways - like many small books bundled together, allowing ions to enter from all directions instead of just one.',
    },
    {
      id: 2,
      title: 'Multi-Ion Solution: Step 2',
      subtitle: 'K⁺ Pillars Expand Layers (Formation)',
      description: 'During initial factory activation, larger potassium ions (K⁺) enter the anode and permanently expand the layers. They act as pillars that keep the pathways open forever.',
    },
    {
      id: 3,
      title: 'Normal Operation',
      subtitle: 'Fast Na⁺ Ion Transport (Charge/Discharge)',
      description: 'With K⁺ pillars permanently in place, smaller sodium ions (Na⁺) can now freely move in and out during charge/discharge cycles. K⁺ stays fixed - only Na⁺ moves, enabling safe 15-minute fast charging.',
    },
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setInterval(() => {
        setActivePhase((prev) => (prev + 1) % phases.length);
        setAnimationKey(k => k + 1);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [isPlaying, phases.length]);

  const handlePhaseChange = (newPhase) => {
    setActivePhase(newPhase);
    setAnimationKey(k => k + 1);
  };

  const replayAnimation = () => {
    setAnimationKey(k => k + 1);
  };

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-gray-900 via-gray-950 to-gray-900 overflow-hidden" data-testid="multiion-animation-section">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/30 mb-5">
            <Atom className="w-4 h-4 text-violet-400" />
            <span className="text-violet-400 text-xs sm:text-sm font-semibold uppercase tracking-wider">Working Principle</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            How <span className="bg-gradient-to-r from-amber-400 to-violet-400 bg-clip-text text-transparent">Multi-Ion</span> Works
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
            K⁺ pillars permanently expand layers, enabling ultra-fast Na⁺ transport
          </p>
        </div>

        {/* Main Animation Container */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Left - SVG Animation */}
          <div className="relative bg-gray-800/30 rounded-2xl border border-white/10 p-6 min-h-[420px]">
            <svg key={animationKey} viewBox="0 0 400 380" className="w-full h-full">
              
              {/* Phase 0: Traditional Problem - Dendrite Formation */}
              {activePhase === 0 && (
                <g>
                  <text x="200" y="25" textAnchor="middle" fill="#f87171" fontSize="14" fontWeight="600">Traditional Li-ion: Dendrite Formation</text>
                  
                  {/* Anode layers (book pages) - tight spacing */}
                  <g>
                    <text x="70" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Anode (tight layers)</text>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <rect key={i} x="30" y={65 + i * 28} width="80" height="20" rx="2" fill="#374151" stroke="#4b5563" strokeWidth="1"/>
                    ))}
                  </g>
                  
                  {/* Fast charging ions - accumulating and forming dendrite */}
                  <g>
                    <text x="200" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Fast Charging</text>
                    
                    {/* Ions moving fast and accumulating */}
                    <circle r="10" fill="#22c55e">
                      <animate attributeName="cx" values="250;120;120" dur="1.5s" fill="freeze"/>
                      <animate attributeName="cy" values="90;90;90" dur="1.5s" fill="freeze"/>
                    </circle>
                    <circle r="10" fill="#22c55e">
                      <animate attributeName="cx" values="270;125;125" dur="1.8s" fill="freeze"/>
                      <animate attributeName="cy" values="110;100;100" dur="1.8s" fill="freeze"/>
                    </circle>
                    <circle r="10" fill="#22c55e">
                      <animate attributeName="cx" values="260;130;130" dur="2s" fill="freeze"/>
                      <animate attributeName="cy" values="130;85;85" dur="2s" fill="freeze"/>
                    </circle>
                    <circle r="10" fill="#22c55e">
                      <animate attributeName="cx" values="280;135;135" dur="2.3s" fill="freeze"/>
                      <animate attributeName="cy" values="100;75;75" dur="2.3s" fill="freeze"/>
                    </circle>
                    <circle r="10" fill="#22c55e">
                      <animate attributeName="cx" values="255;140;140" dur="2.6s" fill="freeze"/>
                      <animate attributeName="cy" values="120;65;65" dur="2.6s" fill="freeze"/>
                    </circle>
                    
                    {/* Dendrite spike growing */}
                    <polygon fill="#ef4444">
                      <animate attributeName="points" values="125,75 125,75 125,75;125,75 145,75 135,35" dur="3s" begin="2s" fill="freeze"/>
                    </polygon>
                    
                    {/* Danger label */}
                    <g opacity="0">
                      <animate attributeName="opacity" values="0;1" dur="0.5s" begin="4s" fill="freeze"/>
                      <text x="135" y="28" textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="bold">DENDRITE!</text>
                    </g>
                  </g>
                  
                  {/* Separator */}
                  <line x1="280" y1="55" x2="280" y2="250" stroke="#60a5fa" strokeWidth="3" strokeDasharray="5,3"/>
                  <text x="280" y="265" textAnchor="middle" fill="#60a5fa" fontSize="9">Separator</text>
                  
                  {/* Cathode */}
                  <rect x="300" y="65" width="80" height="175" rx="5" fill="#7c3aed" opacity="0.3"/>
                  <text x="340" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Cathode</text>
                  
                  {/* Warning box */}
                  <g>
                    <rect x="100" y="290" width="200" height="55" rx="10" fill="#ef4444" opacity="0.15" stroke="#ef4444" strokeWidth="1"/>
                    <text x="200" y="312" textAnchor="middle" fill="#f87171" fontSize="12" fontWeight="600">⚠️ Short Circuit Risk</text>
                    <text x="200" y="330" textAnchor="middle" fill="#fca5a5" fontSize="10">Dendrites puncture separator → Fire/Explosion</text>
                  </g>
                  
                  {/* Legend */}
                  <g>
                    <circle cx="50" cy="360" r="8" fill="#22c55e"/>
                    <text x="70" y="364" fill="#9ca3af" fontSize="10">= Li⁺ ions (can't enter fast)</text>
                  </g>
                </g>
              )}

              {/* Phase 1: Carbon Anode Structure */}
              {activePhase === 1 && (
                <g>
                  <text x="200" y="25" textAnchor="middle" fill="#34d399" fontSize="14" fontWeight="600">Multi-Ion: Carbon Anode</text>
                  
                  {/* Multiple small "books" structure - random orientation */}
                  <g>
                    <text x="100" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Multiple Entry Pathways</text>
                    
                    {/* Randomized book bundles */}
                    <g transform="translate(30, 60) rotate(-15)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    <g transform="translate(80, 70) rotate(10)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    <g transform="translate(50, 120) rotate(-5)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    <g transform="translate(90, 130) rotate(20)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    <g transform="translate(40, 180) rotate(-10)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    <g transform="translate(85, 195) rotate(5)">
                      {[0,1,2,3].map(i => <rect key={i} x={i*4} y={i*10} width="35" height="8" rx="1" fill="#374151" stroke="#4b5563" strokeWidth="0.5"/>)}
                    </g>
                    
                    {/* Arrows showing multiple entry points - animated */}
                    {[80, 120, 160, 200].map((y, i) => (
                      <g key={i}>
                        <line x1="160" y1={y} x2="140" y2={y} stroke="#22c55e" strokeWidth="2" opacity="0">
                          <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin={`${i * 0.3}s`} repeatCount="indefinite"/>
                        </line>
                        <polygon points={`140,${y} 148,${y-5} 148,${y+5}`} fill="#22c55e" opacity="0">
                          <animate attributeName="opacity" values="0;1;0" dur="1.5s" begin={`${i * 0.3}s`} repeatCount="indefinite"/>
                        </polygon>
                      </g>
                    ))}
                  </g>
                  
                  {/* Comparison boxes */}
                  <g>
                    <rect x="200" y="60" width="180" height="90" rx="10" fill="#991b1b" opacity="0.15"/>
                    <text x="290" y="82" textAnchor="middle" fill="#f87171" fontSize="11" fontWeight="600">❌ Traditional Graphite</text>
                    <text x="290" y="100" textAnchor="middle" fill="#fca5a5" fontSize="9">• Single orientation</text>
                    <text x="290" y="115" textAnchor="middle" fill="#fca5a5" fontSize="9">• Limited entry points</text>
                    <text x="290" y="130" textAnchor="middle" fill="#fca5a5" fontSize="9">• Ions queue up → Dendrites</text>
                  </g>
                  
                  <g>
                    <rect x="200" y="160" width="180" height="90" rx="10" fill="#065f46" opacity="0.2"/>
                    <text x="290" y="182" textAnchor="middle" fill="#34d399" fontSize="11" fontWeight="600">✓ Carbon Anode</text>
                    <text x="290" y="200" textAnchor="middle" fill="#86efac" fontSize="9">• Random orientation</text>
                    <text x="290" y="215" textAnchor="middle" fill="#86efac" fontSize="9">• Multiple entry pathways</text>
                    <text x="290" y="230" textAnchor="middle" fill="#86efac" fontSize="9">• Ions spread out → No queue</text>
                  </g>
                  
                  {/* Analogy box */}
                  <g>
                    <rect x="30" y="290" width="340" height="50" rx="10" fill="#1e1b4b" opacity="0.3"/>
                    <text x="200" y="312" textAnchor="middle" fill="#c4b5fd" fontSize="11" fontWeight="500">📚 Analogy: Like many small books bundled randomly</text>
                    <text x="200" y="328" textAnchor="middle" fill="#a78bfa" fontSize="9">vs a single large book with aligned pages</text>
                  </g>
                </g>
              )}

              {/* Phase 2: K+ Pillars - K+ moves INTO anode layers and layers EXPAND */}
              {activePhase === 2 && (
                <g>
                  <text x="200" y="25" textAnchor="middle" fill="#a78bfa" fontSize="14" fontWeight="600">Formation: K⁺ Enters & Expands Layers</text>
                  
                  {/* Anode structure with expanding layers */}
                  <g>
                    <text x="80" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Anode Layers Expanding</text>
                    
                    {/* Layers that expand as K+ enters - animate from tight to wide spacing */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <rect key={i} x="30" width="100" height="14" rx="2" fill="#374151" stroke="#4b5563" strokeWidth="1">
                        <animate attributeName="y" values={`${70 + i * 22};${70 + i * 40}`} dur="2.5s" fill="freeze"/>
                      </rect>
                    ))}
                    
                    {/* K+ ions moving INTO the gaps between layers */}
                    {/* First K+ - enters gap between layer 0 and 1 */}
                    <g>
                      <circle r="12" fill="#8b5cf6">
                        <animate attributeName="cx" values="200;80" dur="1.8s" fill="freeze"/>
                        <animate attributeName="cy" values="100;100" dur="1.8s" fill="freeze"/>
                      </circle>
                      <text fontSize="9" fontWeight="bold" fill="#fff" textAnchor="middle">
                        <animate attributeName="x" values="200;80" dur="1.8s" fill="freeze"/>
                        <animate attributeName="y" values="103;103" dur="1.8s" fill="freeze"/>
                        K⁺
                      </text>
                    </g>
                    
                    {/* Second K+ - enters gap between layer 1 and 2 */}
                    <g>
                      <circle r="12" fill="#8b5cf6">
                        <animate attributeName="cx" values="210;80" dur="2s" begin="0.4s" fill="freeze"/>
                        <animate attributeName="cy" values="140;140" dur="2s" begin="0.4s" fill="freeze"/>
                      </circle>
                      <text fontSize="9" fontWeight="bold" fill="#fff" textAnchor="middle">
                        <animate attributeName="x" values="210;80" dur="2s" begin="0.4s" fill="freeze"/>
                        <animate attributeName="y" values="143;143" dur="2s" begin="0.4s" fill="freeze"/>
                        K⁺
                      </text>
                    </g>
                    
                    {/* Third K+ - enters gap between layer 2 and 3 */}
                    <g>
                      <circle r="12" fill="#8b5cf6">
                        <animate attributeName="cx" values="195;80" dur="2.2s" begin="0.8s" fill="freeze"/>
                        <animate attributeName="cy" values="180;180" dur="2.2s" begin="0.8s" fill="freeze"/>
                      </circle>
                      <text fontSize="9" fontWeight="bold" fill="#fff" textAnchor="middle">
                        <animate attributeName="x" values="195;80" dur="2.2s" begin="0.8s" fill="freeze"/>
                        <animate attributeName="y" values="183;183" dur="2.2s" begin="0.8s" fill="freeze"/>
                        K⁺
                      </text>
                    </g>
                    
                    {/* Fourth K+ - enters gap between layer 3 and 4 */}
                    <g>
                      <circle r="12" fill="#8b5cf6">
                        <animate attributeName="cx" values="205;80" dur="2.4s" begin="1.2s" fill="freeze"/>
                        <animate attributeName="cy" values="220;220" dur="2.4s" begin="1.2s" fill="freeze"/>
                      </circle>
                      <text fontSize="9" fontWeight="bold" fill="#fff" textAnchor="middle">
                        <animate attributeName="x" values="205;80" dur="2.4s" begin="1.2s" fill="freeze"/>
                        <animate attributeName="y" values="223;223" dur="2.4s" begin="1.2s" fill="freeze"/>
                        K⁺
                      </text>
                    </g>
                    
                    {/* Expansion arrows showing layers pushing apart */}
                    <g opacity="0">
                      <animate attributeName="opacity" values="0;1" dur="0.5s" begin="2s" fill="freeze"/>
                      <text x="140" y="95" fill="#a78bfa" fontSize="12">↕</text>
                      <text x="140" y="135" fill="#a78bfa" fontSize="12">↕</text>
                      <text x="140" y="175" fill="#a78bfa" fontSize="12">↕</text>
                      <text x="140" y="215" fill="#a78bfa" fontSize="12">↕</text>
                    </g>
                  </g>
                  
                  {/* Electrolyte */}
                  <rect x="170" y="60" width="50" height="210" rx="5" fill="#1e3a5f" opacity="0.3"/>
                  <text x="195" y="285" textAnchor="middle" fill="#60a5fa" fontSize="9">Electrolyte</text>
                  
                  {/* Cathode */}
                  <rect x="235" y="60" width="150" height="210" rx="5" fill="#7c3aed" opacity="0.15"/>
                  <text x="310" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Cathode</text>
                  
                  {/* Info boxes */}
                  <g>
                    <rect x="240" y="70" width="140" height="80" rx="8" fill="#1e1b4b" opacity="0.5"/>
                    <text x="310" y="92" textAnchor="middle" fill="#a78bfa" fontSize="11" fontWeight="600">How K⁺ Works</text>
                    <text x="310" y="110" textAnchor="middle" fill="#c4b5fd" fontSize="8">1. K⁺ is larger than Na⁺</text>
                    <text x="310" y="124" textAnchor="middle" fill="#c4b5fd" fontSize="8">2. Pushes layers apart as it enters</text>
                    <text x="310" y="138" textAnchor="middle" fill="#c4b5fd" fontSize="8">3. Stays forever as a "pillar"</text>
                  </g>
                  
                  <g>
                    <rect x="240" y="160" width="140" height="50" rx="8" fill="#065f46" opacity="0.3"/>
                    <text x="310" y="180" textAnchor="middle" fill="#34d399" fontSize="10" fontWeight="600">Factory Formation</text>
                    <text x="310" y="198" textAnchor="middle" fill="#86efac" fontSize="8">One-time process • K⁺ never leaves</text>
                  </g>
                  
                  {/* Animated progress indicator */}
                  <g>
                    <rect x="240" y="220" width="140" height="40" rx="8" fill="#78350f" opacity="0.3"/>
                    <text x="310" y="238" textAnchor="middle" fill="#fcd34d" fontSize="9" fontWeight="500">Watch the layers expand! →</text>
                    <text x="310" y="252" textAnchor="middle" fill="#fde68a" fontSize="8">K⁺ creates permanent space</text>
                  </g>
                  
                  {/* Legend */}
                  <g>
                    <rect x="30" y="305" width="350" height="55" rx="8" fill="#1f2937"/>
                    <circle cx="55" cy="332" r="12" fill="#8b5cf6"/>
                    <text x="55" y="336" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="bold">K⁺</text>
                    <text x="150" y="325" fill="#c4b5fd" fontSize="10" fontWeight="500">Larger Ion (K⁺)</text>
                    <text x="150" y="342" fill="#9ca3af" fontSize="8">Expands layers and becomes permanent pillar</text>
                    <rect x="280" y="322" width="90" height="20" rx="4" fill="#374151" stroke="#4b5563"/>
                    <text x="325" y="336" textAnchor="middle" fill="#9ca3af" fontSize="8">Anode Layer</text>
                  </g>
                </g>
              )}

              {/* Phase 3: Normal Operation - Na+ cycles in/out, K+ stays fixed as pillars */}
              {activePhase === 3 && (
                <g>
                  <text x="200" y="25" textAnchor="middle" fill="#34d399" fontSize="14" fontWeight="600">Normal Operation: Na⁺ Cycles Through Expanded Layers</text>
                  
                  {/* Anode with EXPANDED layers and FIXED K+ pillars */}
                  <g>
                    <text x="80" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Anode (permanently expanded)</text>
                    
                    {/* Expanded layers - wide spacing, static */}
                    {[0, 1, 2, 3, 4].map((i) => (
                      <rect key={i} x="30" y={70 + i * 40} width="100" height="14" rx="2" fill="#374151" stroke="#4b5563" strokeWidth="1"/>
                    ))}
                    
                    {/* K+ pillars - FIXED in place between layers, semi-transparent to show permanence */}
                    <g>
                      {/* K+ pillar between layer 0 and 1 */}
                      <circle cx="80" cy="100" r="10" fill="#8b5cf6" opacity="0.5"/>
                      <text x="80" y="103" textAnchor="middle" fill="#e9d5ff" fontSize="7" fontWeight="bold">K⁺</text>
                      
                      {/* K+ pillar between layer 1 and 2 */}
                      <circle cx="80" cy="140" r="10" fill="#8b5cf6" opacity="0.5"/>
                      <text x="80" y="143" textAnchor="middle" fill="#e9d5ff" fontSize="7" fontWeight="bold">K⁺</text>
                      
                      {/* K+ pillar between layer 2 and 3 */}
                      <circle cx="80" cy="180" r="10" fill="#8b5cf6" opacity="0.5"/>
                      <text x="80" y="183" textAnchor="middle" fill="#e9d5ff" fontSize="7" fontWeight="bold">K⁺</text>
                      
                      {/* K+ pillar between layer 3 and 4 */}
                      <circle cx="80" cy="220" r="10" fill="#8b5cf6" opacity="0.5"/>
                      <text x="80" y="223" textAnchor="middle" fill="#e9d5ff" fontSize="7" fontWeight="bold">K⁺</text>
                    </g>
                    
                    {/* Na+ ions cycling IN and OUT through expanded gaps - continuous loop */}
                    {/* Na+ 1 - charges (moves from cathode into anode) */}
                    <g>
                      <circle r="8" fill="#f59e0b">
                        <animate attributeName="cx" values="200;50;50;200" dur="3s" repeatCount="indefinite"/>
                        <animate attributeName="cy" values="100;100;100;100" dur="3s" repeatCount="indefinite"/>
                      </circle>
                      <text fontSize="6" fontWeight="bold" fill="#1f2937" textAnchor="middle">
                        <animate attributeName="x" values="200;50;50;200" dur="3s" repeatCount="indefinite"/>
                        <animate attributeName="y" values="102;102;102;102" dur="3s" repeatCount="indefinite"/>
                        Na⁺
                      </text>
                    </g>
                    
                    {/* Na+ 2 - discharges (moves from anode to cathode) - offset */}
                    <g>
                      <circle r="8" fill="#f59e0b">
                        <animate attributeName="cx" values="50;200;200;50" dur="3s" repeatCount="indefinite"/>
                        <animate attributeName="cy" values="140;140;140;140" dur="3s" repeatCount="indefinite"/>
                      </circle>
                      <text fontSize="6" fontWeight="bold" fill="#1f2937" textAnchor="middle">
                        <animate attributeName="x" values="50;200;200;50" dur="3s" repeatCount="indefinite"/>
                        <animate attributeName="y" values="142;142;142;142" dur="3s" repeatCount="indefinite"/>
                        Na⁺
                      </text>
                    </g>
                    
                    {/* Na+ 3 - charges */}
                    <g>
                      <circle r="8" fill="#f59e0b">
                        <animate attributeName="cx" values="200;50;50;200" dur="2.8s" repeatCount="indefinite"/>
                        <animate attributeName="cy" values="180;180;180;180" dur="2.8s" repeatCount="indefinite"/>
                      </circle>
                      <text fontSize="6" fontWeight="bold" fill="#1f2937" textAnchor="middle">
                        <animate attributeName="x" values="200;50;50;200" dur="2.8s" repeatCount="indefinite"/>
                        <animate attributeName="y" values="182;182;182;182" dur="2.8s" repeatCount="indefinite"/>
                        Na⁺
                      </text>
                    </g>
                    
                    {/* Na+ 4 - discharges */}
                    <g>
                      <circle r="8" fill="#f59e0b">
                        <animate attributeName="cx" values="50;200;200;50" dur="3.2s" repeatCount="indefinite"/>
                        <animate attributeName="cy" values="220;220;220;220" dur="3.2s" repeatCount="indefinite"/>
                      </circle>
                      <text fontSize="6" fontWeight="bold" fill="#1f2937" textAnchor="middle">
                        <animate attributeName="x" values="50;200;200;50" dur="3.2s" repeatCount="indefinite"/>
                        <animate attributeName="y" values="222;222;222;222" dur="3.2s" repeatCount="indefinite"/>
                        Na⁺
                      </text>
                    </g>
                  </g>
                  
                  {/* Electrolyte with bidirectional arrows */}
                  <rect x="140" y="60" width="50" height="210" rx="5" fill="#1e3a5f" opacity="0.3"/>
                  <text x="165" y="285" textAnchor="middle" fill="#60a5fa" fontSize="9">Electrolyte</text>
                  
                  {/* Charge/discharge direction indicators */}
                  <g>
                    <text x="165" y="105" textAnchor="middle" fill="#22c55e" fontSize="14">→</text>
                    <text x="165" y="145" textAnchor="middle" fill="#ef4444" fontSize="14">←</text>
                    <text x="165" y="185" textAnchor="middle" fill="#22c55e" fontSize="14">→</text>
                    <text x="165" y="225" textAnchor="middle" fill="#ef4444" fontSize="14">←</text>
                  </g>
                  
                  {/* Cathode */}
                  <rect x="205" y="60" width="180" height="210" rx="5" fill="#7c3aed" opacity="0.15"/>
                  <text x="295" y="50" textAnchor="middle" fill="#9ca3af" fontSize="10">Cathode</text>
                  
                  {/* Result box */}
                  <g>
                    <rect x="215" y="70" width="80" height="90" rx="8" fill="#065f46" opacity="0.4"/>
                    <text x="255" y="90" textAnchor="middle" fill="#34d399" fontSize="10" fontWeight="600">Results</text>
                    <text x="255" y="108" textAnchor="middle" fill="#86efac" fontSize="8">✓ 15 min charge</text>
                    <text x="255" y="122" textAnchor="middle" fill="#86efac" fontSize="8">✓ No dendrites</text>
                    <text x="255" y="136" textAnchor="middle" fill="#86efac" fontSize="8">✓ 5,000+ cycles</text>
                    <text x="255" y="150" textAnchor="middle" fill="#86efac" fontSize="8">✓ Safe operation</text>
                  </g>
                  
                  {/* Key insight box */}
                  <g>
                    <rect x="305" y="70" width="75" height="90" rx="8" fill="#78350f" opacity="0.3"/>
                    <text x="342" y="90" textAnchor="middle" fill="#fcd34d" fontSize="9" fontWeight="600">Key Insight</text>
                    <text x="342" y="108" textAnchor="middle" fill="#fde68a" fontSize="7">K⁺ = Fixed pillars</text>
                    <text x="342" y="122" textAnchor="middle" fill="#fde68a" fontSize="7">Never move again</text>
                    <line x1="315" y1="132" x2="369" y2="132" stroke="#fcd34d" strokeWidth="0.5" strokeDasharray="2"/>
                    <text x="342" y="145" textAnchor="middle" fill="#f59e0b" fontSize="7">Na⁺ = Mobile</text>
                    <text x="342" y="157" textAnchor="middle" fill="#f59e0b" fontSize="7">Cycles in/out</text>
                  </g>
                  
                  {/* Charge/Discharge explanation */}
                  <g>
                    <rect x="215" y="175" width="165" height="90" rx="8" fill="#1e1b4b" opacity="0.4"/>
                    <text x="297" y="195" textAnchor="middle" fill="#c4b5fd" fontSize="10" fontWeight="600">Charge/Discharge Cycle</text>
                    <text x="297" y="212" textAnchor="middle" fill="#22c55e" fontSize="8">→ CHARGE: Na⁺ moves into anode</text>
                    <text x="297" y="227" textAnchor="middle" fill="#ef4444" fontSize="8">← DISCHARGE: Na⁺ moves to cathode</text>
                    <text x="297" y="245" textAnchor="middle" fill="#9ca3af" fontSize="7">Expanded layers allow fast transport</text>
                    <text x="297" y="257" textAnchor="middle" fill="#9ca3af" fontSize="7">= Safe 15-minute fast charging!</text>
                  </g>
                  
                  {/* Legend */}
                  <g>
                    <rect x="30" y="305" width="355" height="55" rx="8" fill="#1f2937"/>
                    <circle cx="55" cy="328" r="10" fill="#f59e0b"/>
                    <text x="55" y="331" textAnchor="middle" fill="#1f2937" fontSize="6" fontWeight="bold">Na⁺</text>
                    <text x="115" y="322" fill="#fcd34d" fontSize="9" fontWeight="500">Smaller Ion (Na⁺)</text>
                    <text x="115" y="338" fill="#9ca3af" fontSize="7">Moves in/out every cycle</text>
                    
                    <circle cx="210" cy="328" r="10" fill="#8b5cf6" opacity="0.5"/>
                    <text x="210" y="331" textAnchor="middle" fill="#e9d5ff" fontSize="6" fontWeight="bold">K⁺</text>
                    <text x="280" y="322" fill="#c4b5fd" fontSize="9" fontWeight="500">Larger Ion (K⁺)</text>
                    <text x="280" y="338" fill="#9ca3af" fontSize="7">Fixed pillar - never moves</text>
                  </g>
                </g>
              )}
            </svg>
          </div>

          {/* Right - Description & Controls */}
          <div className="space-y-6">
            {/* Phase navigation */}
            <div className="flex items-center justify-between">
              <button 
                onClick={() => handlePhaseChange((activePhase - 1 + phases.length) % phases.length)}
                className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <div className="flex gap-2">
                {phases.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePhaseChange(idx)}
                    className={`h-3 rounded-full transition-all ${
                      activePhase === idx ? 'bg-emerald-400 w-8' : 'bg-gray-600 hover:bg-gray-500 w-3'
                    }`}
                  />
                ))}
              </div>
              <button 
                onClick={() => handlePhaseChange((activePhase + 1) % phases.length)}
                className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Phase content */}
            <div className="p-6 bg-gray-800/50 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  activePhase === 0 ? 'bg-red-500/20' : 
                  activePhase === 1 ? 'bg-emerald-500/20' : 
                  activePhase === 2 ? 'bg-violet-500/20' : 'bg-amber-500/20'
                }`}>
                  <span className={`font-bold text-lg ${
                    activePhase === 0 ? 'text-red-400' : 
                    activePhase === 1 ? 'text-emerald-400' : 
                    activePhase === 2 ? 'text-violet-400' : 'text-amber-400'
                  }`}>{activePhase + 1}</span>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{phases[activePhase].title}</h3>
                  <p className={`text-sm ${
                    activePhase === 0 ? 'text-red-400' : 
                    activePhase === 1 ? 'text-emerald-400' : 
                    activePhase === 2 ? 'text-violet-400' : 'text-amber-400'
                  }`}>{phases[activePhase].subtitle}</p>
                </div>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {phases[activePhase].description}
              </p>
            </div>

            {/* Control buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="flex-1 py-3 bg-gray-800/50 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-emerald-500/30 transition-all flex items-center justify-center gap-2"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <button
                onClick={replayAnimation}
                className="flex-1 py-3 bg-gray-800/50 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-violet-500/30 transition-all flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Replay Phase
              </button>
            </div>

            {/* Ion Legend */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                    <span className="text-gray-900 text-xs font-bold">Na⁺</span>
                  </div>
                  <span className="text-amber-400 font-semibold">Smaller Ion</span>
                </div>
                <p className="text-gray-400 text-xs">Primary charge carrier. Moves in/out during every charge cycle.</p>
              </div>
              <div className="p-4 bg-violet-500/10 rounded-xl border border-violet-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-violet-500/50 flex items-center justify-center">
                    <span className="text-violet-200 text-xs font-bold">K⁺</span>
                  </div>
                  <span className="text-violet-400 font-semibold">Larger Ion</span>
                </div>
                <p className="text-gray-400 text-xs">Pillar ion. Enters once, stays forever. Keeps layers expanded.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default MultiIonAnimationSection;
